import React from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  IconButton,
} from '@mui/material';
import { motion } from 'framer-motion';
import { Email, LinkedIn } from '@mui/icons-material';
import { SectionTitle } from '../common';
import { fadeInUp, staggerContainer, cardHover, viewportConfig } from '../../utils/animations';
import { TEAM_MEMBERS } from '../../utils/constants';

const TeamMemberCard = ({ member, index }) => {
  return (
    <Box
      component={motion.div}
      variants={fadeInUp}
      custom={index}
      initial="rest"
      whileHover="hover"
      animate="rest"
    >
      <Card
        component={motion.div}
        variants={cardHover}
        sx={{
          height: '100%',
          textAlign: 'center',
          cursor: 'pointer',
          border: '1px solid',
          borderColor: 'grey.100',
          overflow: 'visible',
          pt: 6,
          position: 'relative',
        }}
      >
        <Avatar
          src={member.avatar}
          alt={member.name}
          sx={{
            width: 100,
            height: 100,
            position: 'absolute',
            top: -50,
            left: '50%',
            transform: 'translateX(-50%)',
            border: '4px solid',
            borderColor: 'background.paper',
            boxShadow: '0 8px 24px rgba(30, 58, 95, 0.15)',
            fontSize: '2rem',
            backgroundColor: 'primary.main',
            color: 'secondary.main',
          }}
        >
          {member.name.split(' ').map(n => n[0]).join('')}
        </Avatar>

        <CardContent sx={{ pt: 4, pb: 3 }}>
          <Typography
            variant="h5"
            component="h3"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: 0.5,
            }}
          >
            {member.name}
          </Typography>

          <Typography
            variant="subtitle2"
            sx={{
              color: 'secondary.main',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              mb: 2,
            }}
          >
            {member.role}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 2,
              px: 1,
            }}
          >
            {member.bio}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            {member.social?.email && (
              <IconButton
                component="a"
                href={`mailto:${member.social.email}`}
                aria-label={`Email ${member.name}`}
                size="small"
                sx={{
                  backgroundColor: 'rgba(30, 58, 95, 0.05)',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                  },
                }}
              >
                <Email fontSize="small" />
              </IconButton>
            )}
            {member.social?.linkedin && (
              <IconButton
                component="a"
                href={member.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${member.name}'s LinkedIn profile`}
                size="small"
                sx={{
                  backgroundColor: 'rgba(30, 58, 95, 0.05)',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                  },
                }}
              >
                <LinkedIn fontSize="small" />
              </IconButton>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

const Team = () => {
  return (
    <Box
      id="team"
      component="section"
      sx={{
        py: { xs: 10, md: 14 },
        backgroundColor: 'background.default',
      }}
    >
      <Container maxWidth="lg">
        <SectionTitle
          overline="Our Team"
          title="Meet the Experts"
          subtitle="Our dedicated team of professionals is committed to providing exceptional property management services."
        />

        <Box
          component={motion.div}
          initial="hidden"
          whileInView="visible"
          viewport={viewportConfig}
          variants={staggerContainer}
        >
          <Grid container spacing={4} justifyContent="center">
            {TEAM_MEMBERS.map((member, index) => (
              <Grid item xs={12} sm={6} md={3} key={member.id}>
                <TeamMemberCard member={member} index={index} />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default Team;
